import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { CheckCircleFilled } from '@ant-design/icons';
import { AppDispatch, RootState } from '../../store';
import { setVerificationToken } from '../../store/slices/authSlice';
import { resetPassword } from '../../services/authService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, { Header } from '../../components/auth/AuthFormContainer';
import PasswordResetModal from '../../components/auth/PasswordResetModal';
import ButtonGroup from '../../components/common/ButtonGroup';

const { Title, Text } = Typography;

const RequirementsContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 28px;
`;

const RequirementTitle = styled.p`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #1F2937;
  margin: 0 0 8px 0;
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RequirementItem = styled.li<{ passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.passed ? '#0C827A' : '#848787'};
  margin-bottom: 8px;

  .anticon {
    font-size: 14px;
    color: ${props => props.passed ? '#0C827A' : '#848787'};
  }
`;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [form] = Form.useForm();
  const [password, setPassword] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const email = location.state?.email || '';
  const verificationCode = location.state?.verificationCode || '';
  const verificationToken = useSelector((state: RootState) => state.auth.verificationToken);

  useEffect(() => {
    // Redirect if missing required data
    if (!email || !verificationCode || !verificationToken) {
      navigate('/auth/forgot-password');
    }
  }, [email, verificationCode, verificationToken, navigate]);

  // Password validation checks
  const hasMinLength = password.length >= 16;
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasUpperAndLower = /[A-Z]/.test(password) && /[a-z]/.test(password);

  const handleFormChange = () => {
    const values = form.getFieldsValue(['password', 'confirmPassword']);
    const password = values.password?.trim() || '';
    const confirmPassword = values.confirmPassword?.trim() || '';
    const hasMinLength = password.length >= 16;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasUpperAndLower = /[A-Z]/.test(password) && /[a-z]/.test(password);
    const isPasswordsMatch = password === confirmPassword;
    setIsFormValid(hasMinLength && hasNumber && hasSpecialChar && hasUpperAndLower && isPasswordsMatch);
  };

  const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
    try {
      setIsSubmitting(true);
      await resetPassword({
        email,
        verificationToken: verificationToken || '',
        verificationCode,
        password: values.password,
        passwordConfirmation: values.confirmPassword,
      });
      // Show success modal
      setShowModal(true);
      // Navigate to sign in page after modal shows
      setTimeout(() => {
        setIsSubmitting(false);
        setShowModal(false);
        navigate('/auth/signin');
      }, 2500);
      // Clear verificationToken from Redux after successful reset
      setTimeout(() => {
        dispatch(setVerificationToken(null));
      }, 5000);
    } catch (error: any) {
      // Case 1: Validation error - show inline error message
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];
        if (error.data.password && Array.isArray(error.data.password)) {
          fieldErrors.push({
            name: 'password',
            errors: error.data.password,
          });
        }
        if (error.data.passwordConfirmation && Array.isArray(error.data.passwordConfirmation)) {
          fieldErrors.push({
            name: 'confirmPassword',
            errors: error.data.passwordConfirmation,
          });
        }
        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          return;
        }
      }
      // Case 2: Other errors - show toast directly
      message.error(error?.message || 'Failed to reset password. Please try again.');
      console.error('Reset password failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>Reset password</Title>
          <Text className="secondaryText">
            Create a new password
          </Text>
        </Header>

        <Form
          form={form}
          name='resetpassword'
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          layout='vertical'
          autoComplete='off'
          requiredMark={false}
        >
          <Form.Item
            name='password'
            label='New password'
            style={{ marginBottom: 28 }}
            rules={[
              {
                required: true,
                message: 'Please input your new password!',
              },
            ]}
          >
            <Input.Password
              placeholder='*************'
              autoComplete='new-password'
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name='confirmPassword'
            label='Confirm password'
            style={{ marginBottom: 28 }}
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: 'Please confirm your password!',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder='*************'
              autoComplete='new-password'
            />
          </Form.Item>

          <RequirementsContainer>
            <RequirementTitle>Password must contain the following:</RequirementTitle>
            <RequirementsList>
              <RequirementItem passed={hasMinLength}>
                <CheckCircleFilled />
                <span>At least 16 characters</span>
              </RequirementItem>
              <RequirementItem passed={hasNumber}>
                <CheckCircleFilled />
                <span>At least one number</span>
              </RequirementItem>
              <RequirementItem passed={hasSpecialChar}>
                <CheckCircleFilled />
                <span>At least one special character</span>
              </RequirementItem>
              <RequirementItem passed={hasUpperAndLower}>
                <CheckCircleFilled />
                <span>At least one uppercase and lowercase character</span>
              </RequirementItem>
            </RequirementsList>
          </RequirementsContainer>
        </Form>

        <ButtonGroup>
          <Button
            type='default'
            size='large'
            onClick={() => navigate('/auth/verify-otp', { state: { email } })}
          >
            Go back
          </Button>

          <Button
            type='primary'
            htmlType='submit'
            loading={isSubmitting}
            size='large'
            form='resetpassword'
            disabled={!isFormValid}
            >
            Confirm
          </Button>
        </ButtonGroup>

        <PasswordResetModal
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      </AuthFormContainer>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage; 